import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";
import {useCallback, useEffect, useId, useMemo, useState} from "react";

import Banner from "@khanacademy/wonder-blocks-banner";
import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {
    ModalDialog,
    ModalFooter,
    ModalPanel,
} from "@khanacademy/wonder-blocks-modal";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {useQuery} from "@khan/data-gql";
import {GuideStateApi, useGuideState} from "@khan/guide-state";
import * as MobileApp from "@khan/mobile-app-integration";
import {useUserAttributes} from "@khan/shared-core";
import {mediaQueries} from "@khan/shared-styles";

import {DEFAULT_ITEM} from "./guide-closet-default-item.ts";
import {useApplyGuideClosetItemMutation} from "./hooks/use-apply-guide-closet-item-mutation.ts";
import {useGuideClosetItemCatalogue} from "./hooks/use-guide-closet-item-catalogue.ts";
import {useGuideOwnedClosetItems} from "./hooks/use-guide-owned-closet-items.ts";
import {useRedeemGuideClosetItemMutation} from "./hooks/use-redeem-guide-closet-item-mutation.ts";
import {ItemCatalogue} from "./item-catalogue.tsx";
import {ModalHeader} from "./modal-header.tsx";
import {POINTS_QUERY} from "./points-query.ts";
import {Preview} from "./preview.tsx";
import type {Swag} from "./types.ts";

interface Props {
    onClose: () => void;
}

export const GuideClosetModal = ({onClose}: Props): React.ReactElement => {
    const swagId = useGuideState("swagId");
    const hatCatalogueResult = useGuideClosetItemCatalogue();
    const [ownedHatsResult, refetchHatsOwned] = useGuideOwnedClosetItems();
    const [previewItem, setPreviewItem] = useState<Swag | null>(null);
    const attributes = useUserAttributes();
    const [pointsResult] = useQuery(POINTS_QUERY);
    const [redeemClosetItem, redeemStatus] = useRedeemGuideClosetItemMutation();
    const [applyClosetItem, applyStatus] = useApplyGuideClosetItemMutation();
    // The ID used by Screen Readers to identify the dialog title.
    const dialogTitleId = useId();
    const kaid = attributes?.kaid || null;

    const pointsAvailable = useMemo(() => {
        if (
            attributes == null ||
            pointsResult.status !== "success" ||
            ownedHatsResult.status !== "success"
        ) {
            return undefined;
        }

        return ownedHatsResult.data.reduce(
            (pointsAvailable, hatOwned) => pointsAvailable - hatOwned.price,
            pointsResult.data?.user?.points ?? 0,
        );
    }, [attributes, ownedHatsResult, pointsResult]);

    const catalogue = useMemo(() => {
        if (hatCatalogueResult.status !== "success") {
            return [DEFAULT_ITEM];
        }
        return [DEFAULT_ITEM, ...hatCatalogueResult.data];
    }, [hatCatalogueResult]);

    const ownedItemIds = useMemo(() => {
        const ownedItems =
            ownedHatsResult.status === "success" ? ownedHatsResult.data : [];

        return ownedItems.reduce(
            (ownedIds, item) => ownedIds.add(item.id),
            new Set([DEFAULT_ITEM.id]),
        );
    }, [ownedHatsResult]);

    const applyEnabled =
        previewItem !== null &&
        ownedItemIds.has(previewItem.id) &&
        swagId !== previewItem.id;

    const handleRedeemClosetItem = useCallback(async () => {
        const previewItemId = previewItem?.id;

        if (!kaid || !previewItemId) {
            return;
        }

        await redeemClosetItem({kaid, swagId: previewItem.id});
        refetchHatsOwned();
    }, [redeemClosetItem, refetchHatsOwned, kaid, previewItem]);

    const handleApplyClosetItem = useCallback(async () => {
        const kaid = attributes?.kaid || null;
        const previewItemId = previewItem?.id;

        if (!kaid || !previewItemId) {
            return;
        }

        await applyClosetItem({kaid, swagId: previewItem.id});
        GuideStateApi.set("swagId", previewItem.id);
        onClose();
    }, [applyClosetItem, attributes, previewItem, onClose]);

    useEffect(() => {
        if (!previewItem && ownedHatsResult.status === "success") {
            setPreviewItem(
                ownedHatsResult.data.find(({id}) => swagId === id) ?? null,
            );
        }
    }, [swagId, previewItem, ownedHatsResult]);

    MobileApp.Guide.useUXStack("guide-closet", {
        onCancel: onClose,
        canConfirm: applyEnabled,
        onConfirm: handleApplyClosetItem,
        translatedTitle: t`Khanmigo's closet`,
        translatedCancelLabel: t`Maybe later`,
        translatedConfirmLabel: t`Wear this item`,
    });

    const isPreviewItemLocked =
        previewItem != null &&
        pointsAvailable != null &&
        previewItem.price > pointsAvailable &&
        !ownedItemIds.has(previewItem.id);
    return (
        <ModalDialog style={styles.container} aria-labelledby={dialogTitleId}>
            <ModalPanel
                header={
                    <ModalHeader
                        title={t`Khanmigo's closet`}
                        titleId={dialogTitleId}
                        subtitle={
                            <Trans>
                                Redeem your energy points to give Khanmigo a new
                                look. Looking to earn more energy points?{" "}
                                <Link
                                    target="_blank"
                                    href="https://support.khanacademy.org/hc/articles/202487710"
                                >
                                    Here's how
                                </Link>
                            </Trans>
                        }
                    />
                }
                content={
                    <View style={styles.body}>
                        <View style={styles.previewContainer}>
                            {isPreviewItemLocked && (
                                <View style={styles.banner}>
                                    <Banner
                                        kind="warning"
                                        text={t`You need more points to claim this item`}
                                    />
                                </View>
                            )}
                            <Preview
                                pointsAvailable={pointsAvailable}
                                previewItem={previewItem}
                                style={styles.preview}
                                totalPoints={
                                    pointsResult.status === "success"
                                        ? (pointsResult.data?.user?.points ?? 0)
                                        : undefined
                                }
                            />
                        </View>
                        <View style={styles.catalogueWrapper}>
                            <View style={styles.catalogue}>
                                <ItemCatalogue
                                    items={catalogue}
                                    ownedItemIds={ownedItemIds}
                                    pointsAvailable={pointsAvailable}
                                    isRedeemLoading={redeemStatus === "loading"}
                                    selectedItem={previewItem}
                                    onRedeemItem={handleRedeemClosetItem}
                                    onItemSelected={setPreviewItem}
                                />
                            </View>
                        </View>
                    </View>
                }
                footer={
                    <ModalFooter>
                        <View style={styles.footerButtons}>
                            <Button
                                disabled={!applyEnabled}
                                spinner={applyStatus === "loading"}
                                onClick={handleApplyClosetItem}
                            >
                                {t`Wear this item`}
                            </Button>
                            <Strut size={spacing.medium_16} />
                            <Button kind="tertiary" onClick={onClose}>
                                {t`Maybe later`}
                            </Button>
                        </View>
                    </ModalFooter>
                }
            />
        </ModalDialog>
    );
};

const styles = StyleSheet.create({
    container: {
        width: "100%",
        maxWidth: 896, // magic number
        maxHeight: 578, // magic number
        padding: spacing.medium_16,
        [mediaQueries.smOrSmaller]: {
            maxWidth: "100%",
            maxHeight: "100%",
        },
        [mediaQueries.xsOrSmaller]: {
            padding: 0,
        },
    },
    body: {
        flexDirection: "row",
        flexGrow: 1,
        alignSelf: "center",
        height: 0,
        overflow: "hidden",
        minInlineSize: "100%",
        margin: -spacing.xLarge_32,
        [mediaQueries.smOrSmaller]: {
            flexDirection: "column",
            maxWidth: "100%",
            maxHeight: "100%",
            alignItems: "center",
            justifyContent: "center",
        },
    },
    previewContainer: {
        flex: "1 0 auto",
        width: "100%",
        maxWidth: "55%",
        [mediaQueries.smOrSmaller]: {
            maxWidth: "100%",
        },
    },
    preview: {
        height: "100%",
    },
    banner: {
        position: "absolute",
        width: "100%",
        zIndex: 1,
    },
    catalogueWrapper: {
        flexDirection: "column",
        flex: "1 1 auto",
        width: "100%",
        maxWidth: "45%",
        justifyContent: "flex-start",
        paddingBlockStart: spacing.large_24,
        paddingBlockEnd: spacing.large_24,
        paddingInlineStart: spacing.xLarge_32,
        paddingInlineEnd: spacing.xLarge_32,
        [mediaQueries.smOrSmaller]: {
            maxWidth: "100%",
        },
    },
    catalogue: {
        overflowY: "auto",
    },
    footerButtons: {
        flexDirection: "row-reverse",
    },
});
